import { agentStatus } from "@foreman/app-core";
import type { OfficeActivity, OfficeAgent, OfficeTask } from "@foreman/protocol";

/** How an agent looks in the playground: a colour, a body shape and one accessory, per agent program. */
export interface Look {
  color: string;
  shape: string;
  acc: "none" | "antenna" | "ring" | "sprout";
}

const hue = (h: number, l = 0.78, c = 0.12) => `oklch(${l} ${c} ${h})`;
const hash = (s: string) => [...s].reduce((n, ch) => (n * 31 + ch.charCodeAt(0)) >>> 0, 7);

/** The orchestrator: lilac, round, with a sprout. */
export const LEAD_LOOK: Look = { color: "oklch(0.8 0.12 295)", shape: "50%", acc: "sprout" };

const KNOWN: Record<string, { hue: number; l: number; c: number; shape: string; acc: Look["acc"] }> = {
  claude: { hue: 42, l: 0.75, c: 0.13, shape: "34%", acc: "none" },
  codex: { hue: 195, l: 0.79, c: 0.1, shape: "16px", acc: "antenna" },
  antigravity: { hue: 350, l: 0.78, c: 0.12, shape: "50% 50% 44% 44% / 58% 58% 42% 42%", acc: "ring" },
};
const SHAPES = ["40%", "12px", "50% 50% 38% 38%", "30% 70% 60% 40% / 50% 40% 60% 50%"];
const ACCS: Look["acc"][] = ["none", "antenna", "ring"];

/**
 * An agent's look. The orchestrator is always the lilac one; the three known agent programs keep their own colour and shape,
 * nudged a few degrees by the agent's id so two agents of one program are not identical twins; any other program gets a
 * stable look of its own.
 */
export function lookOf(agent: Pick<OfficeAgent, "id" | "provider" | "isGod">): Look {
  if (agent.isGod) return LEAD_LOOK;
  const nudge = (hash(agent.id) % 5) * 7 - 14;
  const k = KNOWN[agent.provider];
  if (k) return { color: hue(k.hue + nudge, k.l, k.c), shape: k.shape, acc: k.acc };
  const h = hash(agent.provider);
  return { color: hue((h % 300) + 20 + nudge, 0.78, 0.11), shape: SHAPES[h % SHAPES.length]!, acc: ACCS[h % ACCS.length]! };
}

export type DeskState = "idle" | "blocked" | "work" | "hand" | "wait" | "fail" | "done" | "paused" | "stopped";
export type Mood = "idle" | "work" | "wait" | "fail" | "done" | "happy";

export const PILL: Record<DeskState, [string, string]> = {
  work: ["rgba(0,202,80,.14)", "#3fdc80"],
  wait: ["rgba(229,165,58,.16)", "#e5a53a"],
  fail: ["rgba(239,106,94,.16)", "#ef6a5e"],
  stopped: ["rgba(239,106,94,.16)", "#ef6a5e"],
  done: ["rgba(237,237,237,.1)", "#ededed"],
  hand: ["oklch(0.8 0.12 295 / .18)", "oklch(0.87 0.08 295)"],
  idle: ["oklch(0.25 0.03 282)", "#a9a8b8"],
  blocked: ["oklch(0.25 0.03 282)", "#a9a8b8"],
  paused: ["oklch(0.25 0.03 282)", "#ededed"],
};

export const MOOD: Record<DeskState, Mood> = { work: "work", wait: "wait", fail: "fail", stopped: "fail", done: "done", hand: "happy", idle: "idle", blocked: "idle", paused: "idle" };

/** An error this recent still shows on the desk. */
export const FAIL_WITHIN_MS = 90_000;

export interface Desk {
  state: DeskState;
  label: string;
  /** For `blocked`: who it is waiting on. */
  waitOn?: string;
  /** The line under the desk: its current step, else what it last did. */
  task: string;
}

/**
 * What a desk shows for one agent: working, waiting on you, handing a step over, tripped on an error, finished, paused,
 * stopped, waiting on a teammate's step, or free. `handing` is true while a hand-over from this agent is in flight.
 */
export function deskOf(agent: OfficeAgent, ctx: { latest?: OfficeActivity; tasks: OfficeTask[]; agents: OfficeAgent[]; handing?: boolean; now?: number }): Desk {
  const now = ctx.now ?? Date.now();
  const mine = ctx.tasks.filter((t) => t.assignee === agent.id);
  const doing = mine.find((t) => t.status === "doing");
  const blockedTask = mine.find((t) => t.status === "blocked" || (t.status === "todo" && (t.dependsOn ?? []).some((d) => ctx.tasks.find((x) => x.id === d)?.status !== "done")));
  const taskLine = doing?.title ?? blockedTask?.title ?? ctx.latest?.title ?? (mine.length ? mine[0]!.title : "Free for a task");
  const base = (state: DeskState, label: string, extra: Partial<Desk> = {}): Desk => ({ state, label, task: taskLine, ...extra });

  if (agent.breaker === "stopped") return base("stopped", "■ Stopped");
  if (agent.onHold) return base("paused", "❚❚ Paused");
  if (ctx.handing) return base("hand", "→ Handing over");
  const st = agentStatus(agent, ctx.latest);
  if (st.status === "needs-you") return base("wait", "! Needs you");
  if (ctx.latest?.kind === "error" && now - ctx.latest.ts < FAIL_WITHIN_MS) return base("fail", "× Hit an error");
  if (st.status === "working" || st.status === "slowed") return base("work", "● Working");
  if (blockedTask && !doing) {
    const dep = (blockedTask.dependsOn ?? []).map((d) => ctx.tasks.find((x) => x.id === d)).find((x) => x && x.status !== "done");
    const who = dep?.assignee ? ctx.agents.find((a) => a.id === dep.assignee)?.name : undefined;
    return base("blocked", who ? `Waiting on ${who}` : "Waiting on the team", { waitOn: who });
  }
  if (mine.length > 0 && mine.every((t) => t.status === "done")) return base("done", "✓ Done");
  return base("idle", "Free");
}

/** The orchestrator's mood: finished, someone needs it, work going on, or at rest. */
export function leadMood(tasks: OfficeTask[], desks: Desk[]): Mood {
  if (tasks.length > 0 && tasks.every((t) => t.status === "done")) return "done";
  if (desks.some((d) => d.state === "wait" || d.state === "fail" || d.state === "stopped")) return "wait";
  if (desks.some((d) => d.state === "work" || d.state === "hand")) return "happy";
  return "idle";
}

/** Halo under a desk, and the monitor's colours, by state. */
export function deskColors(state: DeskState): { halo: string; screen: string; screenFg: string; glyph: string } {
  const halo = state === "work" ? "rgba(0,202,80,.14)" : state === "wait" ? "rgba(229,165,58,.2)" : state === "fail" || state === "stopped" ? "rgba(239,106,94,.2)" : state === "hand" ? "oklch(0.8 0.12 295 / .2)" : "rgba(0,0,0,.28)";
  const screen = state === "wait" ? "rgba(229,165,58,.25)" : state === "fail" || state === "stopped" ? "rgba(239,106,94,.25)" : state === "done" ? "rgba(0,202,80,.2)" : "#0c0b16";
  const screenFg = state === "wait" ? "#e5a53a" : state === "fail" || state === "stopped" ? "#ef6a5e" : state === "done" ? "#00ca50" : "#5c5a6b";
  const glyph = state === "work" ? "" : state === "wait" ? "?" : state === "fail" || state === "stopped" ? "×" : state === "done" ? "✓" : state === "paused" ? "❚❚" : state === "hand" ? "→" : "–";
  return { halo, screen, screenFg, glyph };
}

/** Whiteboard row glyph and colours, by step status. */
export function boardRow(status: string): { glyph: string; gColor: string; tColor: string; pulse: boolean } {
  const done = status === "done";
  return {
    glyph: done ? "✓" : status === "doing" ? "●" : status === "blocked" ? "◷" : "○",
    gColor: done || status === "doing" ? "#00a844" : "#9a98aa",
    tColor: done ? "#8a8898" : "#16141f",
    pulse: status === "doing",
  };
}

/** What kind of message this is, in the words a person would use. */
export function kickerOf(act: string): string {
  return { request: "ASSIGNMENT", done: "REPORT · DONE", inform: "UPDATE", query: "QUESTION", propose: "PROPOSAL", agree: "AGREED", refuse: "DECLINED" }[act] ?? act.toUpperCase();
}

/** File-looking words in a message, for the chips under a hand-over. */
export function refsIn(text: string, max = 5): string[] {
  const found = text.match(/(?:[\w.-]+\/)*[\w.-]+\.[A-Za-z][A-Za-z0-9]{0,5}(?::\d+)?/g) ?? [];
  return [...new Set(found.filter((f) => !/^\d+\.\d+/.test(f) && f.length <= 60))].slice(0, max);
}
